import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { APIQueryResult } from 'shared/api/APIQuery';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { useNullableContext } from 'shared/hooks/useNullableContext';
import { createSearchCriteria } from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { UpdateParams } from 'shared/routing/LocationParams';
import { useLocationParamsLegacy } from 'shared/routing/LocationParamsLegacy';
import { PostingDTO } from '../../data/dto/PostingDTO';
import {
  PostingsPageParamsDTO,
  postingsPageParamsSchema,
} from '../../data/dto/PostingsPageParamsDTO';
import { usePosting } from '../../data/LoadboardPostingAPI';
import { useSavedSearches } from '../../data/LoadboardSavedSearchesAPI';
import { addRecentSearch } from '../saved-searches/PostingRecentSearchesStorage';

function useQueryNormalizer() {
  const [{ savedSearchGuid, activeLoadGuid, loadsType }, setQuery] = useQuery();

  useEffect(() => {
    if (activeLoadGuid || savedSearchGuid || loadsType) {
      setQuery(
        (prev) => ({
          loadsType: undefined,
          activeLoadGuid: undefined,
          savedSearchGuid: undefined,
          current_tab: prev.current_tab || loadsType,
          current_load_guid: prev.current_load_guid || activeLoadGuid,
          current_saved_search_guid:
            prev.current_saved_search_guid || savedSearchGuid,
        }),
        true,
      );
    }
  }, [activeLoadGuid, loadsType, savedSearchGuid, setQuery]);
}

interface PostingsPageContext {
  params: PostingsPageParamsDTO;
  setParams: UpdateParams<PostingsPageParamsDTO>;
  loadSuggestionsNudgingRequestedLoadGuid: string | null;
  setLoadSuggestionsNudgingRequestedLoadGuid: (guid: string | null) => void;
}

const Context = createContext<null | PostingsPageContext>(null);

export function usePostingsPageContext() {
  return useNullableContext('PostingsPage', Context);
}

export function PostingsPageContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [params, setParams] = useLocationParamsLegacy({
    yupSchema: postingsPageParamsSchema,
  });

  const savedSearches = useSavedSearches();

  const currentSavedSearch = useMemo(
    () =>
      savedSearches.data?.find(
        (item) => item.guid === params.current_saved_search_guid,
      ),
    [savedSearches, params.current_saved_search_guid],
  );

  const [
    loadSuggestionsNudgingRequestedLoadGuid,
    setLoadSuggestionsNudgingRequestedLoadGuid,
  ] = useState<string | null>(null);

  const ctx = useMemo(
    (): PostingsPageContext => ({
      params,
      setParams,
      loadSuggestionsNudgingRequestedLoadGuid,
      setLoadSuggestionsNudgingRequestedLoadGuid,
    }),
    [
      params,
      setParams,
      loadSuggestionsNudgingRequestedLoadGuid,
      setLoadSuggestionsNudgingRequestedLoadGuid,
    ],
  );

  useQueryNormalizer();

  useEffect(() => {
    addRecentSearch(params.search_criteria);
  }, [params.search_criteria]);

  useEffect(() => {
    if (params.shipper_guid) {
      setParams((prev) => ({
        shipper_guid: undefined,
        search_criteria: createSearchCriteria({
          ...prev.search_criteria,
          shipper: {
            include: [{ guid: params.shipper_guid, display_name: '' }],
          },
        }),
      }));
    }
  }, [params, setParams]);

  useEffect(() => {
    if (currentSavedSearch) {
      setParams(
        {
          // @ts-expect-error - incompatible types
          search_criteria: currentSavedSearch.search_criteria,
        },
        { navigation: 'replace' },
      );
    }
  }, [setParams, currentSavedSearch]);

  // if saved search is not found then clear it
  useEffect(() => {
    if (
      !currentSavedSearch &&
      !savedSearches.isLoading &&
      params.current_saved_search_guid
    ) {
      setParams({ current_saved_search_guid: undefined });
    }
  }, [
    setParams,
    currentSavedSearch,
    savedSearches.isLoading,
    params.current_saved_search_guid,
  ]);

  return <Context.Provider value={ctx}>{children}</Context.Provider>;
}

export function useCurrentPosting(): APIQueryResult<PostingDTO> {
  const [params] = useLocationParamsLegacy({
    yupSchema: postingsPageParamsSchema,
  });
  return usePosting(params.current_load_guid, params.current_tab);
}
